import React, { useEffect, useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { DashboardLayout } from '../../../components/layout';
import { AreaToolbar, AreaToolbarButton } from '../../../components/layout/area-toolbar';
import { StartPageTable } from '../../../components/start-page-table/start-page-table';
import { useMetaTitle } from '../../../hooks';
import { Restricted } from '../../../authentication';
import { ResourceOperations, ResourceTypes } from '../../../authentication/resources';
import { RegistrationCounterListDtoEx } from '../../../api-service/clients';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { attendanceAreaPathName, getRegistrationCounterCreatePath, getRegistrationCounterEditPath } from '../attendance-area-pathnames';
import { usePostToTab } from '../../search/hooks/usePostToTab';
import { useAttendanceRights } from '../use-attendance-rights';
import { registrationCounterColumns } from './registration-counter-columns';
import { REFRESH_REGISTRATION_COUNTER_TABLE } from './registration-counter-uniform/registration-counter';

const client = moduleClientFactory.createAttendanceClient();

export function RegistrationCountersPage() {
  const [registrationCounters, setRegistrationCounters] = useState<RegistrationCounterListDtoEx[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const { canEditRegistrationCounters } = useAttendanceRights();

  useMetaTitle('Arbejdsområde fremmøde - Infostandere');

  async function loadRegistrationCounters() {
    try {
      setIsLoading(true);
      const result = await client.getRegistrationCounters();
      setRegistrationCounters(result.registrationCounters || []);
    } catch (error) {
      enqueueSnackbar('Der skete en fejl ved hentning af infostandere', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRegistrationCounters();
  }, []);

  usePostToTab(REFRESH_REGISTRATION_COUNTER_TABLE, () => loadRegistrationCounters());

  function handleCreateClick() {
    window.open(`/${attendanceAreaPathName}/${getRegistrationCounterCreatePath()}`);
  }

  function handleRowClick(row: RegistrationCounterListDtoEx) {
    window.open(`/${attendanceAreaPathName}/${getRegistrationCounterEditPath(row.registrationCounterId)}`);
  }

  return (
    <DashboardLayout
      toolbar={
        <AreaToolbar
          mainItemGuid="43f675c0-9629-4ee2-be8f-d02012693834"
          prefixedText="Arbejdsområde fremmøde - "
          tools={
            canEditRegistrationCounters
              ? [
                  <AreaToolbarButton key="add" icon={faPlus} color="primary" variant="text" iconPos="left" onClick={handleCreateClick}>
                    Opret ny infostander
                  </AreaToolbarButton>,
                ]
              : []
          }
        />
      }
    >
      <Restricted
        actions={[
          { resourceType: ResourceTypes.AttendanceFacility, resourceOperation: ResourceOperations.Read },
          { resourceType: ResourceTypes.AttendanceFacility, resourceOperation: ResourceOperations.Write },
        ]}
      >
        <StartPageTable<RegistrationCounterListDtoEx>
          title="Infostandere"
          context="RegistrationCounters"
          universe="company"
          columns={registrationCounterColumns}
          rows={registrationCounters}
          getRowId={row => row.registrationCounterId}
          getRootEntityId={row => row.companyId}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          onRefresh={loadRegistrationCounters}
        />
      </Restricted>
    </DashboardLayout>
  );
}
